import {
	Component,
	OnDestroy,
	OnInit,
	inject,
	ChangeDetectionStrategy,
} from '@angular/core';
import {
	CellValueChangedEvent,
	ColDef,
	GridApi,
	GridReadyEvent,
	SideBarDef,
} from 'ag-grid-community';
import {
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	FormsModule,
} from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from '@lmc-app/core/services/user.service';
import { BankingPortfolioService } from '@lmc-app/core/services/banking-portfolio.service';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { LoanAccountService } from '@lmc-app/core/services/loan-account.service';
import { ApplicationService } from '@lmc-app/core/services/application.service';
import { CurrencyPipe, NgIf } from '@angular/common';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { FundingListToolPanelComponent } from '@lmc-app/custom-table/funding-list-tool-panel/funding-list-tool-panel.component';
import { User } from '@lmc-app/shared/interface/user.interface';
import { Subject, Subscription } from 'rxjs';
import { Loading } from '../../applications/loading';
import { ApplicationSearchService } from '@lmc-app/core/services/application-search.service';
import {
	MatFormField,
	MatLabel,
	MatInput,
	MatHint,
	MatSuffix,
} from '@angular/material/input';
import {
	MatDatepickerInput,
	MatDatepickerToggle,
	MatDatepicker,
} from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatButton } from '@angular/material/button';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
	selector: 'app-funding-list',
	templateUrl: './funding-list.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./funding-list.component.scss'],
	imports: [
		NgIf,
		ReactiveFormsModule,
		MatFormField,
		MatLabel,
		MatInput,
		MatDatepickerInput,
		MatHint,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		FormsModule,
		MatIcon,
		MatIconButton,
		MatButton,
		AgGridAngular,
	],
})
export class FundingListComponent implements OnInit, OnDestroy {
	private parentSub = new Subscription();
	private readonly _onDestroy = new Subject<void>();

	private user: User;
	protected readonly inputDateForm = new FormGroup({
		input_date: new FormControl(null),
	});

	protected readonly headerStyle: any = {
		display: 'flex',
		'justify-content': 'space-between',
	};
	protected readonly title: string = 'Funding List';

	protected columnDefs: ColDef[] = [
		{
			field: 'si',
			headerName: 'SI',
			width: 50,
			pinned: 'left',
		},
		{
			field: 'loan_number',
			headerName: 'Loan Number',
			width: 150,
			pinned: 'left',
		},
		{
			field: 'name',
			headerName: 'AC NO',
			width: 230,
			pinned: 'left',
			cellStyle: (params) => {
				let style = null;
				switch (params.data.status) {
					case 'Validated':
					case 'Funded':
						style = { backgroundColor: '#3aff17' };
						break;
					default:
						break;
				}
				return style;
			},
		},
		{
			field: 'acct_note',
			headerName: 'Note',
			width: 130,
			editable: true,
		},
		{
			headerName: 'SAHA CHQ',
			width: 130,
			valueGetter: (params) => {
				return this.currencyFormatter.transform(
					params.data['SAHA CHQ'],
					'CAD',
					'symbol-narrow'
				);
			},
		},
		{
			headerName: 'Portfolio',
			width: 120,
			valueGetter: (params) => params.data.bankingPortfolio?.name,
			valueSetter: (params) => {
				params.data.bankingPortfolio = this.bankingPortfolios.find(
					(bankingPortfolio) => bankingPortfolio.name === params.newValue
				);
				return true;
			},
			editable: true,
			cellEditor: 'agSelectCellEditor',
			cellEditorParams: {
				values: [],
			},
		},
		{
			headerName: 'Amount',
			width: 120,
			editable: true,
			valueGetter: (params) => {
				return this.currencyFormatter.transform(
					params.data.amount,
					'CAD',
					'symbol-narrow'
				);
			},
			valueSetter: (params) => {
				params.data.amount = Number(
					params.newValue.replace('$', '').replace(',', '')
				);
				return true;
			},
			cellStyle: (params) => {
				let style = {};
				if (params.data.amount !== params.data.original_note_amount) {
					style = { color: 'red' };
				}
				switch (params.data.status) {
					case 'Funded':
						style = { ...style, backgroundColor: '#3aff17' };
						break;
					default:
						break;
				}
				return style;
			},
		},
		{
			field: 'exception_note',
			headerName: 'Exception',
			width: 150,
			editable: true,
			cellStyle: (params) =>
				params.value ? { backgroundColor: '#fc9d9d' } : null,
		},
		{
			field: 'LPP',
			headerName: 'LPP',
			width: 80,
		},
		{
			field: 'LPP NQ',
			headerName: 'LPP NQ',
			width: 80,
			valueGetter: (params) => (params.data['LPP NQ'] ? 'Yes' : ''),
			valueSetter: (params) => {
				params.data['LPP NQ'] = params.newValue === 'Yes';
				return true;
			},
			cellStyle: (params) => {
				let style = {};
				if (params.data.original_lpp_nq !== params.data['LPP NQ']) {
					style = { color: 'red' };
				}
				return style;
			},
			editable: true,
			cellEditor: 'agSelectCellEditor',
			cellEditorParams: {
				values: ['', 'Yes'],
			},
		},
		{
			field: 'application_source',
			headerName: 'OBS',
			width: 100,
			editable: true,
			cellEditor: 'agSelectCellEditor',
			cellEditorParams: {
				values: ['', 'RAPID', 'Manual'],
			},
		},
		{
			field: 'rejection_note',
			headerName: 'Rejection',
			width: 200,
			editable: true,
		},
		{
			field: 'status',
			headerName: 'Status',
			width: 160,
			editable: true,
			cellEditor: 'agSelectCellEditor',
			cellEditorParams: {
				values: ['', 'Funding Rejection', 'Validated', 'Funded'],
			},
		},
	];

	private bankingPortfolios: IdName[] = [];
	protected rowData: { status: string }[] = [];
	private sum = {
		'SAHA CHQ': 0,
	};

	private readonly _bottomSheet = inject(MatBottomSheet);
	private readonly _snackBar = inject(MatSnackBar);

	protected sideBar: SideBarDef | string | string[] | boolean | null;

	public constructor(
		private readonly apiService: LegacyApiService,
		private readonly userService: UserService,
		private readonly bankingPortfolioService: BankingPortfolioService,
		private readonly loanAccountService: LoanAccountService,
		private readonly currencyFormatter: CurrencyPipe,
		private readonly applicationService: ApplicationService,
		private readonly _appSearchService: ApplicationSearchService
	) {}

	public ngOnInit(): void {
		this.parentSub.add(
			this.userService.user$.subscribe((user) => {
				this.user = user;
			})
		);
		this.setFundingAccount([]);
		this.inputDateForm.controls.input_date.setValue(new Date());
		this.getPortfolios();
		this.getFundingList(
			this.inputDateForm.controls.input_date.value
				.toISOString()
				.substring(0, 10)
		);
	}

	public ngOnDestroy(): void {
		this.parentSub.unsubscribe();
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	private getPortfolios() {
		this.parentSub.add(
			this.bankingPortfolioService.getBankingPortfolios().subscribe({
				next: (res) => {
					this.bankingPortfolios = res;
					this.columnDefs = this.columnDefs.map((colDef) => {
						if (colDef.headerName === 'Portfolio') {
							colDef.cellEditorParams.values = [
								'',
								...this.bankingPortfolios.map(
									(bankingPortfolio) => bankingPortfolio.name
								),
							];
						}
						return colDef;
					});
				},
				error: (error) => {
					console.error(error);
				},
			})
		);
	}

	protected onInputDateChanged(inputDate: Date) {
		this.getFundingList(inputDate.toISOString().substring(0, 10));
	}

	protected onCellValueChanged(e: CellValueChangedEvent<any>) {
		this._bottomSheet.open(Loading, {
			disableClose: true,
			data: { message: 'Saving' },
		});
		const loanAccount = {
			...e.data,
			banking_portfolio_id: e.data.bankingPortfolio?.banking_portfolio_id,
		};
		if (e.data.status === 'Funding Rejection') {
			this.saveStageChange(e.data.cifnumber);
		} else {
			this.parentSub.add(
				this.loanAccountService.updateLoanAccount(loanAccount).subscribe({
					next: () => {
						this.getFundingList(
							this.inputDateForm.controls.input_date.value
								.toISOString()
								.substring(0, 10)
						);
						this._snackBar.open(
							`Loan account for ${loanAccount.name} is updated.`,
							SnackbarConstants.ACTION_DISMISS,
							SnackbarConstants.DURATION_DISMISS_SHORT
						);
						this._bottomSheet.dismiss();
					},
					error: (error) => {
						this.getFundingList(
							this.inputDateForm.controls.input_date.value
								.toISOString()
								.substring(0, 10)
						);
						this._bottomSheet.dismiss();
						console.error(error);
						this._snackBar.open(
							(error.error &&
								error.error.message &&
								(error.error.message.name || error.error.message)) ||
								'Error updating loan account.',
							SnackbarConstants.ACTION_DISMISS,
							SnackbarConstants.DURATION_DISMISS_SHORT
						);
					},
				})
			);
		}
	}

	private getFundingList(inputDate: string) {
		this.parentSub.add(
			this.loanAccountService
				.getLoanAccounts(inputDate)
				.subscribe((res: any) => {
					if (res.length) {
						this.loanAccountService
							.getDailyTarget(inputDate)
							.subscribe((resDailyTarget: any) => {
								this.setFundingAccount(res, resDailyTarget['daily_target']);
							});
					} else {
						this.setFundingAccount([]);
					}
				})
		);
	}

	private setFundingAccount(
		data: {
			status: string;
			'SAHA CHQ': number;
			bankingPortfolio: { name: string };
			amount: number;
		}[],
		dailyTarget = undefined
	) {
		this.rowData = data;
		const fundedList = data.filter((deal) => deal.status === 'Funded');
		this.sum = {
			'SAHA CHQ': 0,
		};
		this.bankingPortfolios.forEach((portfolio) => {
			this.sum[portfolio.name] = fundedList
				.filter(
					(loanAccount) => loanAccount.bankingPortfolio?.name === portfolio.name
				)
				.reduce((acc, curr) => acc + curr.amount, 0);
		});
		this.sum['Portfolio Total'] = Object.values(this.sum).reduce(
			(acc: any, cur: any) => acc + cur,
			0
		);
		this.sum['SAHA CHQ'] = fundedList.reduce(
			(acc: number, curr: { 'SAHA CHQ': number }) => acc + curr['SAHA CHQ'],
			0
		);
		this.sum['Agency Referal Total'] =
			this.sum['Portfolio Total'] - this.sum['SAHA CHQ'];
		if (dailyTarget) {
			this.sum['Daily Target'] = dailyTarget;
			this.sum['RFR'] = dailyTarget - this.sum['Portfolio Total'];
		}
		this.setSummaryData();
	}

	private setSummaryData() {
		const summaryData = [];
		for (const [label, amount] of Object.entries(this.sum)) {
			summaryData.push({
				label,
				value: this.currencyFormatter.transform(amount, 'CAD', 'symbol-narrow'),
				primary: label === 'Portfolio Total',
			});
		}
		this.sideBar = {
			toolPanels: [
				{
					id: 'filters',
					labelDefault: 'Filters',
					labelKey: 'filters',
					iconKey: 'filter',
					toolPanel: 'agFiltersToolPanel',
				},
				{
					id: 'columns',
					labelDefault: 'Columns',
					labelKey: 'columns',
					iconKey: 'columns',
					toolPanel: 'agColumnsToolPanel',
					toolPanelParams: {
						suppressPivotMode: true,
						suppressValues: true,
						suppressRowGroups: true,
					},
				},
				{
					id: 'customStats',
					labelDefault: 'Summary',
					labelKey: 'customStats',
					iconKey: 'chart',
					width: 280,
					toolPanel: FundingListToolPanelComponent,
					toolPanelParams: {
						summaryData,
						footnote: '*RFR: Remaining Funding Requirement',
					},
				},
			],
			defaultToolPanel: 'customStats',
		};
	}

	private saveStageChange(applicationId: string): void {
		const newStage = 'Underwriting Checklist';

		const note = {
			subject: 'Funding Rejection',
			description: '-',
			communication: 'INT',
			note: '',
		};

		// User submitted the form
		this.onStageChangeNoteSubmit(applicationId, newStage, note);
	}

	private onStageChangeNoteSubmit(
		applicationId: string,
		newStage: string,
		noteData: any
	): void {
		this.parentSub.add(
			this.applicationService.getApplication(applicationId).subscribe({
				next: (res) => {
					const application = res;
					if (application.Deal_Name) {
						this._appSearchService
							.insertPageHistory(
								application.Deal_Name,
								application.id,
								this.user.email
							)
							.subscribe((res) => {
								if (!res?.status) {
									console.log(res, 'Error insert page history!');
								}
							});
					}
					this.applicationService
						.updateApplicationStageWithNote(
							applicationId,
							newStage,
							noteData,
							application.Stage_History_ID,
							this.user.zoho_owner_id,
							application.Deal_Name,
							this.user.full_name
						)
						.subscribe({
							next: (res) => {
								if (res.statusCode === 200) {
									this.getFundingList(
										this.inputDateForm.controls.input_date.value
											.toISOString()
											.substring(0, 10)
									);
									this._snackBar.open(
										`Loan account for ${application.Deal_Name} is updated.`,
										SnackbarConstants.ACTION_DISMISS,
										SnackbarConstants.DURATION_DISMISS_SHORT
									);
									this._bottomSheet.dismiss();
								} else {
									console.error('Stage update failed:', res);
								}
							},
							error: (error) => {
								console.error('Stage update error:', error);
							},
						});
				},
				error: (error) => {
					console.error(error);
					this._snackBar.open(
						error,
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_SHORT
					);
				},
			})
		);
	}

	private gridApi: GridApi<any>;

	protected filterValue: string = '';
	protected tooltipShowDelay = 0;
	protected tooltipHideDelay = 2000;

	protected overlayLoadingTemplate =
		'<span class="ag-overlay-loading-center">Loading Data...</span>';
	protected overlayNoRowsTemplate =
		'<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;">This is a custom \'no rows\' overlay</span>';

	protected defaultColDef: ColDef = {
		sortable: true,
		resizable: true,
		filter: true,
		wrapHeaderText: true,
		autoHeaderHeight: true,
	};

	protected onGridReady(params: GridReadyEvent) {
		this.gridApi = params.api;
		this.gridApi.showLoadingOverlay();
	}

	protected onFilterTextBoxChanged() {
		this.gridApi.setQuickFilter(this.filterValue);
	}

	protected onFilterClear() {
		this.filterValue = '';
		this.onFilterTextBoxChanged();
	}

	protected resetFilter() {
		this.gridApi?.setFilterModel(null);
		this.onFilterClear();
	}
}
